import type { Folder } from '@/lib/api/folders'
import type { Template } from '@/types/domain'

/**
 * The Projekte screen's data shaping, kept pure so the rules below can be
 * asserted instead of clicked.
 *
 * The one fact that shapes everything here: **projects do not nest.** The
 * `folders` table (`gateway/migrations/00001_init_schema.sql`) has no
 * `parent_id`, `models.Folder` has no parent, and `GET /v1/folders` returns
 * a flat list. The artboard draws a project inside a project and a row
 * counted as "2 Projekte"; that tree cannot be stored, so it is not built.
 * What is built is the level the schema does hold — a project with its forms
 * under it — in a component that would take a deeper tree the day the column
 * exists.
 */

/** A project with the forms filed in it. One level, deliberately. */
export interface ProjectNode {
  folder: Folder
  templates: Template[]
  /** Forms filed here. Derived: the gateway reports no per-folder counts. */
  templateCount: number
  /** Scans across those forms, from each list item's `document_count`. */
  documentCount: number
  updatedAt: number | null
  /** More than one person can reach it — see `isShared`. */
  shared: boolean
}

export interface ProjectsView {
  projects: ProjectNode[]
  /** Forms in no project the caller can see: the "Ohne Projekt" section. */
  unfiled: Template[]
}

/**
 * Milliseconds from whatever the gateway sent.
 *
 * Two vocabularies meet on this screen: a folder carries ISO strings
 * (`created_at`/`updated_at`), a template the worker's **microsecond** epoch
 * (`TemplateListItem.UpdatedAtMicros`, serialised as `updatedAt`). The
 * magnitude is the only discriminator available, and the cut-offs below are
 * safe for every date this product can hold: 1e14 µs is 1973, 1e11 s is the
 * year 5138.
 */
export function toMillis(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null
    if (value > 1e14) return Math.round(value / 1000)
    if (value < 1e11) return Math.round(value * 1000)
    return value
  }
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const dateFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** The date column. Empty rather than a placeholder when nothing was sent —
 *  a made-up date on a row people sort by is worse than a blank cell. */
export function formatDate(value: number | string | null | undefined): string {
  const ms = toMillis(value)
  return ms == null ? '' : dateFormat.format(new Date(ms))
}

/**
 * Whether more than one person can reach this project.
 *
 * Not "mit mir geteilt", which the artboard's third tab promises: the folder
 * list carries `created_by` in the model but the Postgres backend never fills
 * it (`folders.PostgresService.hydrate`), so "did someone else share this
 * with me, or did I share it with them" is not answerable. `members` is
 * filled, so "more than one person" is — and that is what the tab says.
 */
export function isShared(folder: Folder): boolean {
  const members = folder.members ? Object.keys(folder.members) : []
  if (members.length > 0) return members.length > 1
  const acl = folder.access_control
  if (!acl) return false
  const uids = new Set([...(acl.read ?? []), ...(acl.write ?? []), ...(acl.admin ?? [])])
  return uids.size > 1
}

/**
 * A form counts as unfiled when its `folder` names no project the caller can
 * see.
 *
 * `''` and the sentinel `'default'` are the gateway's two ways of saying "no
 * project" (`normalizeTemplate` defaults the missing key to `'default'`). An
 * id that names a real folder the caller cannot list is folded in here too:
 * it would otherwise vanish from the screen entirely, and a form nobody can
 * find is worse than one offered a project.
 */
export function isUnfiled(folderId: string | undefined, knownFolderIds: Set<string>): boolean {
  if (!folderId || folderId === 'default') return true
  return !knownFolderIds.has(folderId)
}

export function buildProjects(folders: Folder[], templates: Template[]): ProjectsView {
  const known = new Set(folders.map((folder) => folder.id))
  const byFolder = new Map<string, Template[]>()
  const unfiled: Template[] = []

  for (const template of templates) {
    if (isUnfiled(template.folder, known)) {
      unfiled.push(template)
      continue
    }
    const list = byFolder.get(template.folder as string)
    if (list) list.push(template)
    else byFolder.set(template.folder as string, [template])
  }

  const projects = folders.map<ProjectNode>((folder) => {
    const own = (byFolder.get(folder.id) ?? []).sort((a, b) => compareName(a.title, b.title))
    return {
      folder,
      templates: own,
      templateCount: own.length,
      documentCount: own.reduce((sum, template) => sum + (template.documentCount ?? 0), 0),
      updatedAt: toMillis(folder.updated_at ?? folder.created_at),
      shared: isShared(folder),
    }
  })

  return { projects, unfiled: unfiled.sort((a, b) => compareName(a.title, b.title)) }
}

function compareName(a: string, b: string): number {
  return a.localeCompare(b, 'de', { sensitivity: 'base' })
}

export type ProjectTab = 'recent' | 'favorites' | 'shared'

/** Newest project first, undated last — an undated row floating to the top of
 *  a "Zuletzt" list would claim a recency nothing told us about. */
export function sortByRecent(projects: ProjectNode[]): ProjectNode[] {
  return [...projects].sort((a, b) => {
    if (a.updatedAt == null && b.updatedAt == null) return compareName(a.folder.name, b.folder.name)
    if (a.updatedAt == null) return 1
    if (b.updatedAt == null) return -1
    return b.updatedAt - a.updatedAt
  })
}

export function applyTab(
  projects: ProjectNode[],
  tab: ProjectTab,
  favorites: ReadonlySet<string>,
): ProjectNode[] {
  switch (tab) {
    case 'favorites':
      return projects.filter((project) => favorites.has(project.folder.id))
    case 'shared':
      return projects.filter((project) => project.shared)
    default:
      return projects
  }
}

export function matches(text: string, query: string): boolean {
  return text.toLocaleLowerCase('de').includes(query)
}

/**
 * The search filter.
 *
 * A project matching by name keeps all of its forms; a project that does not
 * is kept only for the forms that match, so typing a form's name reveals
 * where it is filed instead of emptying the screen. The filter sees the
 * projects and forms that were loaded and nothing else — see
 * `TEMPLATE_PAGE_SIZE` in `useProjects`, and the note the screen renders when
 * that page is full.
 */
export function filterProjects(projects: ProjectNode[], rawQuery: string): ProjectNode[] {
  const query = rawQuery.trim().toLocaleLowerCase('de')
  if (!query) return projects
  const hits: ProjectNode[] = []
  for (const project of projects) {
    if (matches(project.folder.name, query)) {
      hits.push(project)
      continue
    }
    const templates = project.templates.filter((template) => matches(template.title, query))
    if (templates.length > 0) hits.push({ ...project, templates })
  }
  return hits
}

export function filterTemplates(templates: Template[], rawQuery: string): Template[] {
  const query = rawQuery.trim().toLocaleLowerCase('de')
  if (!query) return templates
  return templates.filter((template) => matches(template.title, query))
}

/** "3 Vorlagen" / "1 Vorlage" — the artboard's count column. */
export function templateCountLabel(count: number): string {
  return count === 1 ? '1 Vorlage' : `${count} Vorlagen`
}

export function documentCountLabel(count: number): string {
  return count === 1 ? '1 Dokument' : `${count} Dokumente`
}
